import { type DisplayLanguage, visibleTabs } from '@amzn/journeys-common';
import type { AssetModel } from 'generated/graphql';
import { type FC, type ReactNode, useEffect } from 'react';
import { Field, useForm } from 'react-final-form';
import { ImageFormInput, type ImageFormInputProps } from '../../features/assets/ImageFormInput';
import type { ImageSelectorModalContainerProps } from '../../features/assets/ImageSelectorModalContainer';
import type { CampaignFormValues } from '../../features/campaign/campaign.types';
import type { SummaryGroup } from '../../features/filters/filters.util';
import type { CommonFormInputProps, FormGroupProps } from './form.types';

export interface JFormImageProps
  extends CommonFormInputProps,
    FormGroupProps,
    Pick<ImageSelectorModalContainerProps, 'uploadFileToS3'>,
    Pick<ImageFormInputProps, 'recommendedMaxSize' | 'type' | 'overrideClasses'> {
  filterParams?: SummaryGroup[]; // predefined filters for the image selector modal
  validFileTypes?: string[];
  language: DisplayLanguage;
  projectId: string;
  canRemove?: boolean;
  containerClassName?: string;
  iconLabel?: string;
  isLowRes?: boolean;
  /** Must have `position: "absolute"` to work! */
  imageOverlay?: ReactNode;
  resetFieldStateWhenValidateChanges?: boolean;
}

/**
 * This is a JFormImage validate function factory you can use to check dimensions and weight.
 */
export const getImageValidator =
  ({ requiredHeight, requiredWidth, maxSize }: { requiredWidth: number; requiredHeight: number; maxSize: number }) =>
  (value: AssetModel): string | undefined => {
    const validMessage = `Image must be ${requiredWidth} x ${requiredHeight}, max weight ${maxSize / 1000}KB.`;
    const errors: string[] = [];
    if (!value) {
      return validMessage;
    }
    if (!value.size) {
      return 'Image weight is missing, please choose image again.';
    }
    if (!value.origWidth) {
      return 'Image width is missing, please choose image again.';
    }
    if (!value.origHeight) {
      return 'Image height is missing, please choose image again.';
    }
    if (!Number.isNaN(maxSize) && value.size && value.size > maxSize) {
      errors.push('Invalid weight.');
    }
    if (!Number.isNaN(requiredWidth) && value.origWidth !== requiredWidth) {
      errors.push('Invalid width.');
    }
    if (!Number.isNaN(requiredHeight) && value.origHeight !== requiredHeight) {
      errors.push('Invalid height.');
    }
    if (errors.length) {
      return `${errors.join(' ')} ${validMessage}`;
    }
    return;
  };

const prettifyErrors = (errors?: string | object) => {
  if (!errors) return;

  if (typeof errors === 'string') return errors;

  return Object.values(errors).toString();
};
/**
 * An image selector as a form input. It sets its form value as the AssetModel type.
 */
export const JFormImage: FC<JFormImageProps> = ({
  name,
  filterParams = [],
  projectId,
  disabled,
  canRemove,
  optional,
  language,
  validFileTypes,
  uploadFileToS3,
  validate,
  recommendedMaxSize,
  label,
  containerClassName,
  overrideClasses: cssOverrides,
  iconLabel,
  type,
  isLowRes = false,
  imageOverlay,
  resetFieldStateWhenValidateChanges = false,
}) => {
  const form = useForm<CampaignFormValues>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    // From docs: "Resets all of a field's flags (e.g. touched, visited, etc.) to their initial state."
    // TODO - This should not happen. No time to fix before code freeze, but I'll fix it after. - jack
    if (resetFieldStateWhenValidateChanges) {
      form.resetFieldState(name as keyof CampaignFormValues);
    }
  }, [
    validate,
    resetFieldStateWhenValidateChanges,
  ]);
  return (
    <Field<AssetModel> name={name} validate={validate}>
      {(fieldProps) => (
        <ImageFormInput
          canRemove={Boolean(canRemove)}
          containerClassName={containerClassName}
          disabled={Boolean(disabled)}
          filterParams={filterParams}
          iconLabel={iconLabel}
          imageOverlay={imageOverlay}
          invalid={Boolean(fieldProps.meta.invalid)}
          invalidFeedback={prettifyErrors(fieldProps.meta.error)}
          isLowRes={isLowRes}
          label={typeof label === 'string' ? label : undefined}
          language={language}
          onBlur={fieldProps.input.onBlur}
          onChange={fieldProps.input.onChange}
          onFocus={fieldProps.input.onFocus}
          optional={optional}
          overrideClasses={cssOverrides}
          projectId={projectId}
          recommendedMaxSize={recommendedMaxSize}
          type={type}
          uploadFileToS3={uploadFileToS3}
          validFileTypes={validFileTypes}
          value={fieldProps.input.value}
          visibleTabs={visibleTabs}
        />
      )}
    </Field>
  );
};
